<template>
  <Card headless>
    <template #body>
      <div class="divide-y *:pt-4 md:flex-row md:divide-y-0 *:md:pt-0">
        <div
          class="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-4 md:border-b md:mb-4"
        >
          <h4>
            <div class="flex items-center">
              <ArchivedLabel
                :disabled="membershipProgram.is_active"
                data-cy="membershipProgramName"
              >
                {{ membershipProgram.name }}
              </ArchivedLabel>
              <a
                :href="membershipProgramShowPath(membershipProgram.id)"
                data-cy="membershipLink"
                ><i class="fas fa-edit link edit-icon ml-1"></i
              ></a>
            </div>
          </h4>
          <div class="flex gap-3 items-center">
            <QuickBooksMapping
              v-if="membershipProgram.qbo_mapping"
              class="mapping"
              :item="membershipProgram"
              @unlink-success="handleUnlinkSuccess"
            />
            <ToggleSwitch v-model="active" label-on="Active" />
          </div>
        </div>
        <div
          class="grid grid-flow-row md:grid-flow-col auto-cols-auto gap-4 divide-y *:pt-4 md:flex-row md:divide-x md:divide-y-0 *:md:pl-4 *:md:pt-0"
        >
          <div class="item-section center-text-section md:-ml-4 -mt-4 md:mt-0">
            <div class="mb-3"><b>Members</b></div>
            <div>
              <div class="member-count">
                {{ membershipProgram.member_count }}
              </div>
              <div class="mb-3 text-gray"><b>Active</b></div>
            </div>
            <div>
              <div class="member-count">
                {{ membershipProgram.expired_count }}
              </div>
              <div class="text-gray"><b>Former Members</b></div>
            </div>
          </div>
          <div class="item-section">
            <div class="mb-2">
              <div>
                <b>Base Price: </b> {{ dollars(membershipProgram.price_cents) }}
              </div>
            </div>
            <div class="mb-2">
              <div>
                <b>Taxable (original sale)</b>
                <span>: {{ membershipProgram.taxable ? "Yes" : "No" }}</span>
              </div>
            </div>
            <div class="mb-2">
              <div>
                <b>Taxable (renewal)</b>
                <span>
                  : {{ membershipProgram.taxable_on_renewal ? "Yes" : "No" }}
                </span>
              </div>
            </div>
            <div class="mb-2">
              <div>
                <b>Additional Systems: </b>
                {{ dollars(membershipProgram.additional_unit_price_cents) }}
              </div>
            </div>
            <div class="mb-2">
              <div>
                <b>Duration: </b>
                {{ displayMembershipDuration(membershipProgram) }}
              </div>
            </div>
            <div v-if="showAccountingCategories" class="mb-2">
              <div>
                <b>Accounting Category: </b>
                {{ membershipProgram.accounting_category_name || "--" }}
              </div>
            </div>
            <div v-if="showAccountingClasses" class="mb-2">
              <div>
                <b>Default Accounting Class: </b>
                {{ membershipProgram.accounting_class_name || "--" }}
              </div>
            </div>
          </div>
          <div class="item-section flex-grow-2">
            <div class="mb-2"><b>Description</b></div>
            <div class="pre-line">{{ membershipProgram.description }}</div>
          </div>
          <div class="item-section flex-grow-2">
            <div class="mb-2"><b>Available Tasks Per Contract Year</b></div>
            <div v-if="!membershipProgram.items.length">--</div>
            <div v-for="item in membershipProgram.items" :key="item.item_id">
              {{ item.quantity }}
              {{ item.unit_name }}
            </div>
          </div>
          <div class="item-section">
            <div class="mb-2"><b>Department Pricing</b></div>
            <div
              v-for="department in membershipProgram.price_sheets"
              :key="department.id"
              class="mb-2"
            >
              <div>
                <b>{{ department.department_name }}: </b>
                {{
                  getPriceSheetName(
                    department.department_id,
                    department.department_membership_price_sheet_id
                  )
                }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";

import Card from "@/components/Card.vue";
import FeatureFlag from "@/components/FeatureFlag.vue";
import ToggleSwitch from "@/components/ToggleSwitch.vue";
import QuickBooksMapping from "@/Quickbooks/QuickBooksMapping.vue";

export default {
  components: {
    QuickBooksMapping,
    FeatureFlag,
    Card,
    ToggleSwitch
  },
  props: {
    membershipProgram: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      active: this.membershipProgram.is_active
    };
  },
  computed: {
    ...mapGetters(["showAccountingCategories", "showAccountingClasses"]),
    ...mapState(["priceSheets"])
  },
  watch: {
    active(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.handleActiveChange();
      }
    }
  },
  methods: {
    ...mapActions(["fetchMembershipPrograms", "updateMembershipProgram"]),
    getPriceSheetName(deptId, id) {
      const deptPriceSheet = this.priceSheets[deptId];
      if (!deptPriceSheet) {
        return "--";
      }
      const priceSheet = deptPriceSheet.find((sheet) => sheet.id === id);
      return priceSheet ? priceSheet.name : "--";
    },
    handleActiveChange() {
      this.updateMembershipActive();
    },
    handleUnlinkSuccess() {
      this.fetchMembershipPrograms();
    },
    updateMembershipActive() {
      this.updateMembershipProgram({
        membership: {
          id: this.membershipProgram.id,
          is_active: this.active
        }
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.flex-grow-2 {
  flex-grow: 2;
}

.italic-text {
  font-style: italic;
}

.edit-icon {
  font-size: 0.9rem;
  position: relative;
  top: -5px;
}

.program-type-text {
  color: var(--gray);
  font-style: italic;
}

.center-text-section {
  text-align: center;
}

.member-count {
  font-size: 2.5rem;
  line-height: 40px;
}

.mapping {
  position: relative;
  top: 2px;
}
</style>
